import { LOXONE_ROW_ID, SERVICEFEE_ROW_ID } from "src/utils/constants";
import { ts } from 'src/utils/translate';
import { GetQuoteResponse } from 'src/model/api/quote/get-quote-response.model';
import { QuoteProductData } from 'src/model/quote-product-data.model';
import { formatPriceForcedCurrency } from 'src/utils/formatPrice';
import { meterToFoot } from 'src/utils/imperialConverter';
import { GetProjectTreeProjectChildNode } from 'src/model/api/get-project-tree-project-childnode.model';
import { getFloorIdForRoom, getFloorNameForRoom } from 'src/utils/projectTreeAdapterForOfferPage';
import { _does } from 'src/components/Planner/planner/uMath';
import { dlInlineSVG_HTML } from 'src/utils/controllerAdapterForOfferPage';
import { ProjectQuoteUserProperties } from 'src/model/api/project.model';
import { QuoteProductColumn } from "src/model/quote-product-column.model";
import { calcImageURL, getFrontendURL } from 'src/utils/url';

export class QuoteConverter {

  // customizationButtons: any;
  projectQuoteUserProperties: ProjectQuoteUserProperties;

  constructor(projectQuoteUserProperties: ProjectQuoteUserProperties) {
    this.projectQuoteUserProperties = projectQuoteUserProperties;
  }

  getSwitches() {
    return {
      IncludeCables: true,
      IncludeElectricalCabinet: true,
      IncludeServices: true,
      IncludeSections: true,
      IncludeCustomRows: true,
    };
  }

  processColumns(input: QuoteProductColumn[], quoteMode: boolean, quantityUnit: string|null = null) {
    /**
     * Process existing columns
     */
    input = input.map((col) => {
      const mappedCol = Object.assign({}, col);

      (mappedCol as any).render = (self: any, full: any) => {
        return self || "";
      };

      (mappedCol as any).cellCallback = null;

      if (
        _does([
          "manufacturer_name",
          "color_name",
          "user_manufacturer_name",
          "design",
        ]).contain(mappedCol.data)
      ) {
        mappedCol.visible = false;
      } else if (
        mappedCol.data === "price_eur" ||
        mappedCol.data === "price_huf"
      ) {
        mappedCol.visible = false;
        mappedCol.width = 150;
        const curr = mappedCol.data.replace("price_", "");
        (mappedCol as any).render = (self: any, full: any) =>
          formatPriceForcedCurrency(self, curr);
        mappedCol.numeric = true;
      } else if (mappedCol.data === "quantity" || mappedCol.data === "count") {
        mappedCol.width = quantityUnit ? 150 : 125;
        mappedCol.numeric = true;
        if (quantityUnit) {
          (mappedCol as any).render = (data: any, full: any, extraProps: any) => {
            if (
              quantityUnit === "m" &&
              "cable_length" in full &&
              extraProps.measurementSystem === "metric"
            ) {
              return `${data} ${ts("q_unit", "unit")} = ${full.cable_length}m`;
            } else if (
              quantityUnit === "m" &&
              "cable_length" in full &&
              extraProps.measurementSystem === "imperial"
            ) {
              const cableLenFt = Math.round(meterToFoot(full.cable_length));
              return `${data} ${ts("q_unit", "unit")} = ${cableLenFt}ft`;
            } else {
              return data;
            }
          };
        }
      } else if (mappedCol.data === "category_name") {
        mappedCol.width = 150;
      } else if (mappedCol.data === "custom_description") {
        mappedCol.visible = false;
      } else if (mappedCol.data === "article_number") {
        mappedCol.visible = false;
      }

      return mappedCol;
    });

    /**
     * Add new columns
     */
    //add design
    input.push({
      data: "design",
      title: ts("q_design", "Design"),
      visible: false,
    } as QuoteProductColumn);

    //add discount
    input.push({
      title: ts("quote_discount", "Discount"),
      data: "discount",
      width: 100,
      render: (self: any, full: any) => {
        return (
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              placeholder="0"
              min="0"
              max="100"
              step="5"
              onWheel={(e) => e.preventDefault()}
              onChange={(e) => {
                /* console.log(e.target.value) */
              }}
            />
            <span className="input-group-addon">%</span>
          </div>
        );
      },
    } as unknown as QuoteProductColumn);

    const selectedCurrency = this.projectQuoteUserProperties.selected_currency

    input.push({
      title: `${ts("net", "Net")} ${selectedCurrency.toUpperCase()}`,
      data: "net_price",
      visible: this.projectQuoteUserProperties.include_currency_visibility === 2,
      numeric: true,
      width: 150,
      render: (self: any, full: any) => {
        return formatPriceForcedCurrency(
          full[`price_${this.projectQuoteUserProperties.selected_currency}`],
          this.projectQuoteUserProperties.selected_currency
        );
      },
    } as unknown as QuoteProductColumn);

    if (input.findIndex((column) => column.data === "quantity")) {
      const quantity = input.splice(
        input.findIndex((column) => column.data === "quantity"),
        1
      );
      input.push(quantity[0]);
    }

    //add totalprice
    input.push({
      title: ts("quote_total_price", "Net Total Price"),
      data: "total_price",
      visible: this.projectQuoteUserProperties.include_currency_visibility === 2,
      numeric: true,
      width: 150,
      render: (self: any, full: any) => {
        return formatPriceForcedCurrency(
          full.quantity *
            full[`price_${this.projectQuoteUserProperties.selected_currency}`],
            this.projectQuoteUserProperties.selected_currency
        );
      },
    } as unknown as QuoteProductColumn);

    return input;
  }

  gatherAndProcessRoomRows(input: QuoteProductData, quoteMode: boolean) {
    let products = input["products"] || [];
    let frames = input["frames"] || [];
    let devices = input["quote_room_devices"] || [];
    let customDevices = input["room_user_custom_items"] || [];

    this.processRows(products, quoteMode);
    this.processRows(frames, quoteMode);
    this.processRows(devices, quoteMode);
    this.processRows(this.preProcessRowsUserCustomItems(customDevices), quoteMode);

    return products.concat(frames as any[]).concat(devices).concat(customDevices);
  }

  preProcessRowsUserCustomItems(rows: any) {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }

    rows.forEach(function (row) {
      row.quantity = row.count || "";
      row.image_url = row.quote_image_url || "";
      row.catalog_page_url = row.details || "";
    });
    return rows;
  }

  processRows(rows: any, quoteMode: boolean) {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }

    rows.forEach(function (row) {
      const fullhost = `${getFrontendURL()}/`;

      if (typeof row.catalog_page_url == "string") {
        row.catalog_page_url = row.catalog_page_url.trim();
        if (row.catalog_page_url.length == 0) {
          row.catalog_page_url = null;
        }
      }
      if (typeof row.image_url == "string") {
        row.image_url = row.image_url.trim();
        if (row.image_url.length == 0 || row.image_url === "-") {
          row.image_url = null;
        } else if (row.image_url.indexOf("ima") == 0) {
          row.image_url = `${fullhost}${row.image_url}`;
        }
      }
      if (typeof row.icon == "string") {
        if (row.icon.indexOf("ima") == 0) {
          row.icon = `${fullhost}${row.icon}`;
        }
      }
      if (typeof row.product_name == "string") {
        row.product_name = row.product_name
          .replace(LOXONE_ROW_ID, "")
          .replace(SERVICEFEE_ROW_ID, "");
        row.deleteIfZero = true;
      }
      if (row.user_manufacturer_name) {
        row.manufacturer_name = row.user_manufacturer_name;
      }

      row.is_back_box = isRowBackBox(row);
      //setQuoteRowDesignColor(row);
    });

    rows = rows.filter(function (row) {
      if (row.deleteIfZero && +row.price_eur === 0 && +row.price_huf === 0) {
        //return false;
      }
      return true;
    });

    const switches = this.getSwitches();

    if (!switches.IncludeCustomRows) {
      rows = rows.filter((row: any) => !row.custom);
    }

    return rows;
  }

  convToArrIfObj(arr: any): any[]|null {
    if (Array.isArray(arr)) {
      return arr;
    } else if (typeof arr === "object") {
      const ret = [];
      for (let i in arr) {
        ret.push(arr[i]);
      }
      return ret;
    }
    return null;
  }

  processRooms(data: GetQuoteResponse, quoteMode: boolean, projectTreeNodes: GetProjectTreeProjectChildNode[]): any[] {
    let custDescCol =
      data &&
      data["quote-project-custom"] &&
      data["quote-project-custom"]["columns"] &&
      data["quote-project-custom"]["columns"].find(function (obj: any) {
        return obj.data == "custom_description";
      });

    let src = data["quote-products"];

    if (custDescCol &&
        !src.columns.find((column) => column.data === "custom_description")) {
      src.columns.push(custDescCol);
    }

    let cols = this.processColumns(src["columns"], quoteMode);

    let seenFloors: any[] = [];
    const rooms = src.data
      .map((room) => {
        const roomname = room.room_name;
        const floorname = getFloorNameForRoom(room.room_id, projectTreeNodes);
        const floorid = getFloorIdForRoom(room.room_id, projectTreeNodes);
        const isJustUnassignedFloor = room.is_just_unassigned_floor;

        (room as any).function_indicators_count =
          this.convToArrIfObj(room.function_indicators_count) || [];
        (room as any).room_custom_items_indicator =
          this.convToArrIfObj(room.room_custom_items_indicator) || [];

        room.room_custom_items_indicator.forEach((elem) => {
          if (!elem.icon.includes('<svg')) {
            const inlinedSvg = dlInlineSVG_HTML(calcImageURL(elem.icon), elem.icon_color);
            if (inlinedSvg) {
              elem.icon = inlinedSvg;
              elem.isSVG = true;
              elem.path = $(elem.icon).find("path");
              elem.fill = elem.path.attr("fill");
              elem.d = elem.path.attr("d");
              elem.viewBox = $(elem.icon).attr("viewBox");
            }
          }
        });

        const roomRows = this.gatherAndProcessRoomRows(room, quoteMode);
        const indicatorRows = this.processRows(
          room.function_indicators_count.concat(
            room.room_custom_items_indicator
          ),
          quoteMode
        );

        let isFirstOfFloor = false;
        if (seenFloors.indexOf(floorname) == -1) {
          isFirstOfFloor = true;
          seenFloors.push(floorname);
        }

        return [
          {
            entitytype: "roomtable_count",
            isFirstOfFloor,
            floorname,
            floorid, //will be deprecated
            floorId: floorid,
            name: roomname,
            id: room.room_id,
            area:
              room.room_area && room.room_area != 0
                ? Math.round((room.room_area / 10000) * 100) / 100
                : null,
            airspace:
              room.room_airspace && room.room_airspace != 0
                ? Math.round(room.room_airspace * 100) / 100
                : null,
            columns: [
              {
                data: "icon",
                name: "icon",
                title: " ",
                width: "35px",
                render: (c: any, full: any) => {
                  if (!full.isSVG) {
                    /* let width = typeof c === "string" && c.endsWith(".svg") ? 40 : 20; */
                    return (
                      <div style={{ width: 20, height: 20 }}>
                        <img
                          /* style={{ width }} */ className="quote-funcount-icon"
                          src={calcImageURL(c)}
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div style={{ width: 20, height: 20 }}>
                        <svg
                          className="quote-funcount-icon"
                          viewBox={full.viewBox}
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d={full.d} fill={full.fill}></path>
                        </svg>
                      </div>
                    );
                  }
                },
              },
              {
                data: "category_name",
                name: "category_name",
                title: ts("quote_thead_category_name", "quoteTHeadCategoryName"),
                render: (a: any) => a,
              },
              {
                data: "count",
                name: "count",
                title: ts("quote_thead_count", "quoteTHeadCount"),
                render: (a: any) => a,
                width: "35px",
                numeric: true,
              },
            ],
            rows: indicatorRows,
          },
          {
            floorId: floorid,
            entitytype: "roomtable",
            roomName: roomname,
            floorName: floorname,
            id: room.room_id,
            columns: cols,
            rows: roomRows,
            isJustUnassignedFloor: isJustUnassignedFloor,
          },
        ];
      })
      .reduce((p, c) => p.concat(c), []);

    rooms
      .filter((room) => room.isFirstOfFloor)
      .forEach((room: any) => {
        room.hasRows = !!rooms.find(
          (fRoom) =>
            fRoom.entitytype.indexOf("roomtable") === 0 &&
            fRoom.id === room.id &&
            fRoom.rows.length !== 0
        );
      });

    return rooms;
  }

  respToTables(data: GetQuoteResponse, projectId: string, quoteMode: boolean = false,
               projectTreeNodes: GetProjectTreeProjectChildNode[]) {
    const roomTables = this.processRooms(data, quoteMode, projectTreeNodes);
    const eCabRoot = (data && data["quote-devices"]) || {
      columns: [],
      data: [],
      loxoneDinDeviceUnit: 0.0,
    };
    const eCabCustomsRoot = (data && data["quote-electrical-cabinet-custom-items"]) || {
      columns: [],
      data: [],
    };

    const eCabConvRoot = (data && data["quote-conventional-devices"]) || {
      columns: [],
      data: [],
    };
    const eCabConvCustomsRoot = (data && data[
      "quote-conventional-devices-custom-items"
    ]) || { columns: [], data: [], conventionalDeviceUnitsInMM: 0.0 };

    const eNHCDinDevicesRoot = (data && data["quote-nhc-devices"]) || {
      columns: [],
      data: [],
      NHCDINDeviceUnit: 0,
    };
    const eNHCDinCustomDevicesRoot = (data && data["quote-nhc-custom-devices"]) || {
      columns: [],
      data: [],
    };

    const rackRoot = (data && data["quote-rack-cabinet-items"]) || {
      columns: [],
      data: [],
      unit_sum: 0,
    };
    const rackCustomsRoot = (data && data["quote-rack-cabinet-custom-items"]) || {
      columns: [],
      data: [],
    };

    const cablesRoot = (data && data["quote-cables"]) || { columns: [], data: [] };
    const cableCustomsRoot = (data && data["quote-cable-custom-items"]) || {
      columns: [],
      data: [],
    };

    const pServicesRoot = (data && data["quote-project-custom"]) || {
      columns: [],
      data: [],
    };
    const sectionsRoot = (data && data["quote-project-custom-section"]) || {
      columns: [],
      data: [],
    };

    let custDescCol =
      data &&
      data["quote-project-custom"] &&
      data["quote-project-custom"]["columns"] &&
      data["quote-project-custom"]["columns"].find(function (obj: any) {
        return obj.data == "custom_description";
      });

    if (custDescCol && !eCabRoot.columns.find((column: any) => column.data === "custom_description")) {
          eCabRoot.columns.push(custDescCol)
    }

    cablesRoot["columns"] = cablesRoot["columns"].filter(function (col: any) {
      return !_does(["category_name", "color_name"]).contain(col.data);
    });

    const unitNumberLoxone = eCabRoot.loxoneDinDeviceUnit / 17.5;
    const formattedUnitLoxone = `${eCabRoot.loxoneDinDeviceUnit.toFixed(2)}mm ~ ${Math.ceil(unitNumberLoxone)}`;

    const unitNumberConventional =
      eCabConvRoot.conventionalDeviceUnitsInMM / 17.5;
    const formattedUnitConventional = `${eCabConvRoot.conventionalDeviceUnitsInMM.toFixed(2)}mm ~ ${Math.ceil(unitNumberConventional)}`;

    const eCabinet = {
      entitytype: "functiontable",
      name: ts("din_device", "DIN devices"),
      id: projectId,
      columns: this.processColumns(eCabRoot.columns, quoteMode),
      rows: this.processRows(
        eCabRoot["data"].concat(eCabCustomsRoot["data"]),
        quoteMode
      ),
      unit_sum: `${ts("q_sum_unit", "SUM. SPACE")}: ${formattedUnitLoxone}`,
    };

    const eCabinetConv = {
      entitytype: "convfunctiontable",
      name: ts(
        "quote_eoutlet_conv",
        "Conventional devices in the electrical cabinet"
      ),
      id: projectId,
      columns: this.processColumns(eCabConvRoot.columns, quoteMode),
      rows: this.processRows(
        eCabConvRoot["data"].concat(eCabConvCustomsRoot["data"]),
        quoteMode
      ),
      unit_sum: `${ts(
        "q_sum_unit",
        "SUM. SPACE"
      )}: ${formattedUnitConventional}`,
    };

    const eNHCDinDevices = {
      entitytype: "nhcdintable",
      name: ts("quote_nhc_din_devices", "NHC DIN Devices"),
      id: projectId,
      columns: this.processColumns(eNHCDinDevicesRoot.columns, quoteMode),
      rows: this.processRows(
        eNHCDinDevicesRoot["data"].concat(eNHCDinCustomDevicesRoot["data"]),
        quoteMode
      ),
      unit_sum: `${ts("q_sum_unit", "SUM. SPACE")}: ${
        eNHCDinDevicesRoot.NHCDINDeviceUnit
      }`,
    };

    const pRack = {
      entitytype: "racktable",
      name: ts("it_products", "IT Products"),
      id: projectId,
      columns: this.processColumns(rackRoot["columns"], quoteMode),
      rows: this.processRows(
        rackRoot["data"].concat(rackCustomsRoot["data"]),
        quoteMode
      ),
      unit_sum: `${ts("q_sum_unit", "SUM. SPACE")}: ${rackRoot.unit_sum}`,
    };

    const pCables = {
      entitytype: "projectcablestable",
      name: `${ts("q_cables", "Cables")} (${
        ts("q_estimated_calc", "Estimated calculation")
      })`,
      id: projectId,
      columns: this.processColumns(cablesRoot["columns"], quoteMode, "m"),
      rows: this.processRows(
        cablesRoot["data"].concat(cableCustomsRoot["data"]),
        quoteMode
      ),
    };

    const pServices = {
      entitytype: "projectcustomtable",
      name: ts("labour_cost", "Labour Cost"),
      id: projectId,
      columns: this.processColumns(pServicesRoot["columns"], quoteMode),
      rows: this.processRows(pServicesRoot["data"], quoteMode),
    };

    const pSections = sectionsRoot.data.map((sectionData: any) => ({
      entitytype: "sectiontable",
      name: sectionData.section_name,
      id: sectionData.id,
      columns: this.processColumns(sectionsRoot["columns"], quoteMode),
      rows: this.processRows(
        sectionData["custom_section_items"] || [],
        quoteMode
      ),
    }));

    let result = roomTables;

    const switches = this.getSwitches();

    if (switches.IncludeElectricalCabinet) {
      result = result.concat(eCabinet);
    }
    result = result.concat(eCabinetConv);
    result = result.concat(pRack);
    result = result.concat(eNHCDinDevices);
    if (switches.IncludeCables) {
      result = result.concat(pCables);
    }
    result = result.concat(pSections);
    if (switches.IncludeServices) {
      result = result.concat(pServices);
    }

    return result;
  }
}

export function isRowBackBox(row: any) {
  if (typeof row !== "object" || !row) { return false; }
  return typeof row.quote_category === "string" && row.quote_category === "back-boxes";
}

